import { ChartDataOptionsBuilder } from './chart-data-options-builder';
import { ChartSeriesDataOptionsBuilder } from './chart-series-data-options-builder';
import { ChartPointDataOptionsBuilder } from './chart-point-data-options-builder';
import { FavouriteDataAdapter } from '../common/favourite-data-adapter';
import { AggregationType, FavouriteRowValue, SortOrderType } from '../common/favourite-interfaces';
import { ChartLayoutConfig } from '../common/layout-config';
import { FavouriteDataOptions } from '../common/favourite-data-options';
import { wrapText, escapeHtml, setTickInterval } from '../common/utils';
import { convertNumericColorToHex } from 'app/infrastructure/universal/color';
import { DrillDownHandler } from '../common/drill-down-builder';
import { getHostingPath } from 'app/infrastructure/hosting';
import { ColumnType, defaultChartHeight, maxChartHeight } from '../../constants';

import styles from './chart-favourite.scss';
import { SeriesTypeColumnMappings, SeriesTypeBaseIndex } from 'app/components/favourite-layout/favourite/chart-data-options/types';
import { XAxisOptions } from 'highcharts';
import { MIN_NO_OF_CRITERIAS } from 'app/area/reports/containers/chart/components/attributes/constants';
import { reportTypes } from 'app/area/favourite/constants';

export class DefaultChartDataOptionsBuilder implements ChartDataOptionsBuilder {

  private readonly DEFAULT_ICON_DIMENSIONS: number = 16;
  private dataOptions: FavouriteDataOptions;
  private dataAdapter: FavouriteDataAdapter;

  public columnMap: SeriesTypeColumnMappings;
  public seriesTypeBaseIndex: SeriesTypeBaseIndex = {};

  public pointDataOptionsBuilder: ChartPointDataOptionsBuilder;
  public seriesDataOptionsBuilder: ChartSeriesDataOptionsBuilder;

  public createAxisHandler: (colIndex: number, axisObj: Highcharts.AxisOptions, axisName: string) => void;
  public createFakeAxisHandler: (options: Highcharts.Options, axisType: string) => void;
  public legendTitleHandler: (seriesCol: number) => string;

  constructor(
    dataOptions: FavouriteDataOptions,
    dataAdapter: FavouriteDataAdapter,
    seriesDataOptionsBuilder: ChartSeriesDataOptionsBuilder,
    pointDataOptionsBuilder: ChartPointDataOptionsBuilder,
    columnMappings: SeriesTypeColumnMappings
  ) {
    this.dataAdapter = dataAdapter;
    this.seriesDataOptionsBuilder = seriesDataOptionsBuilder;
    this.pointDataOptionsBuilder = pointDataOptionsBuilder;
    this.dataOptions = dataOptions;
    this.columnMap = this.createColumnMap(columnMappings);

    // We override the extremes based on target - Only used for charts
    if (this.dataAdapter.hasTarget()) {
      const { columnExtremes, targetColIndex, aggrColIndex } = this.dataAdapter;
      const minTargetCol = columnExtremes[targetColIndex].min;
      const minAggrCol = columnExtremes[aggrColIndex].min;
      if (minTargetCol < minAggrCol) {
        columnExtremes[aggrColIndex].min = minTargetCol;
      }

      const maxTargetCol = columnExtremes[targetColIndex].max;
      const maxAggrCol = columnExtremes[aggrColIndex].max;
      if (maxTargetCol > maxAggrCol) {
        columnExtremes[aggrColIndex].max = maxTargetCol;
      }
    }
  }

  public useHtml(colIndex: number) {
    return this.dataAdapter.hasCategories(colIndex)
      && (this.dataAdapter.hasBgColors(colIndex) || this.dataAdapter.hasIcons(colIndex))
      && !(this.dataAdapter.layoutConfig as ChartLayoutConfig).chartStyle('disableHtmlLabels');
  }

  public anyHtmlColumn(): boolean {
    for (let i = 0; i < this.dataAdapter.dataModel.columns.length; i++) {
      if (this.useHtml(i)) {
        return true;
      }
    }
    return false;
  }

  public createIconOrColourDiv(colIndex: number, value: FavouriteRowValue,
    emptyPlaceholder: boolean = true, overrideColor: string = null): string {
    if (this.dataAdapter.hasIcons(colIndex) && value && value.iconUrl) {
      return '<img class="' + styles.categoryIcon + '" src="' +
        getHostingPath() + '/' + value.iconUrl +
        '" style="width:' + this.DEFAULT_ICON_DIMENSIONS + 'px;height:' + this.DEFAULT_ICON_DIMENSIONS + 'px;">';
    }
    if (this.dataAdapter.hasBgColors(colIndex) &&
      !this.dataAdapter.hasIcons(colIndex) &&
      value && value.bgColour
    ) {
      return '<div class="colorBox" style="background-color:' +
        (overrideColor ? overrideColor : convertNumericColorToHex(value.bgColour)) + '"></div>';
    }
    if (emptyPlaceholder) { // Return div to take up space even if there is nothing to display
      return '<div class="colorBox"></div>';
    }
    return '';
  }

  public addSpanWrapper(str: string, marginRight: number) {
    return '<span style="vertical-align:middle;' + 'margin-right:' + marginRight + 'px">' + str + '</span>';
  }

  private createColumnMap(columnMappings: SeriesTypeColumnMappings): SeriesTypeColumnMappings {
    const seriesTypeMappings: SeriesTypeColumnMappings = { mainSeries: [] };
    Object.keys(columnMappings).forEach((seriesType: string) => {
      const columnList: any[] = [];
      for (let c = 0; c < this.dataAdapter.dataModel.columns.length; c++) {
        const col = this.dataAdapter.dataModel.columns[c];
        if (col.columnType === 'selection') {
          const selColName = 'sel' + (c + 1).toString();
          columnList.push(columnMappings[seriesType][selColName]);
        } else {
          columnList.push(columnMappings[seriesType][col.columnType]);
        }
      }
      seriesTypeMappings[seriesType] = columnList;
    });
    return seriesTypeMappings;
  }

  public setOptions(options: Highcharts.Options, splitRow: FavouriteRowValue, seriesValues: FavouriteRowValue[]) {
    const splitCol = this.colIndexOf('splitting');
    const seriesCol = this.colIndexOf('series');
    const xIndex = this.colIndexOf('x', 'mainSeries');
    this.prepareSeries(options, splitCol, splitRow, seriesCol, seriesValues);
    this.pointDataOptionsBuilder.createDataPoints(
      options, seriesCol, seriesValues, this.seriesTypeBaseIndex, this.columnMap, xIndex);

    // Data is returned sorted, but Highcharts requires the x-value to always be ascending.
    // We use the sortOrderType and xAxis.reverse later to present data as descending.
    this.reverseDescendingData(options, xIndex);

    this.removeEmptyData(options);
    this.setHeight(options);
  }

  public isLegendEnabled(seriesCol: number): boolean {
    return !(seriesCol < 0 && this.dataAdapter.targetColIndex < 0 && this.dataAdapter.aggrRollAvgColIndex < 0);
  }

  public isShowValueEnabled(): boolean {
    const pieShowValueAttribute = this.dataOptions.layoutConfig.layoutAttributes.Pie?.ShowValue;
    return pieShowValueAttribute !== false;
  }

  public isShowPercentEnabled(): boolean {
    const pieShowPercentAttribute = this.dataOptions.layoutConfig.layoutAttributes.Pie?.ShowPercent;
    return pieShowPercentAttribute !== false;
  }

  public isHideDatalabelsEnabled(): boolean {
    const pieHideDatalabels = this.dataOptions.layoutConfig.layoutAttributes.Pie?.HideDatalabelsOnDashboards;
    const showValueAndPercentDisabled = !this.isShowValueEnabled() && !this.isShowPercentEnabled();
    const isInDashboardContext = this.dataOptions.layoutConfig.layoutAttributes.Highcharts.plotOptions.pie?.showInLegend;
    return isInDashboardContext && (pieHideDatalabels !== false || showValueAndPercentDisabled);
  }

  public useLegendTitle(): boolean {
    return this.dataAdapter.selectionCount > 1;
  }

  public legendTitle(seriesCol: number): string {
    return this.legendTitleHandler
      ? this.legendTitleHandler(seriesCol)
      : this.dataOptions.getColumnTitle(this.colIndexOf(seriesCol < 0 ? 'x' : 'series'), 'legend');
  }

  public addLegend(options: Highcharts.Options, seriesCol: number): void {
    if (this.useLegendTitle()) {
      let titleText =
        wrapText((this.dataAdapter.layoutConfig as ChartLayoutConfig), this.legendTitle(seriesCol),
          'legendTitleLengthLimit', 'legendTitleTruncLimit');
      options.legend.title = { text: titleText };
      options.legend.alignColumns = false;

      if (this.dataAdapter.hasTarget() && this.columnMap.targetSeries && (this.dataAdapter.selectionCount >= MIN_NO_OF_CRITERIAS)) {
        titleText =
          wrapText(
            (this.dataAdapter.layoutConfig as ChartLayoutConfig),
            this.dataOptions.getColumnTitle(this.dataAdapter.targetColIndex,
              'legend'
            ),
            'legendTitleLengthLimit', 'legendTitleTruncLimit');
        options.legend.title.text += '<br />' + titleText;
      }
    }

    const layoutAttributes = this.dataAdapter.layoutConfig.layoutAttributes;
    if (layoutAttributes.Highcharts && layoutAttributes.Highcharts.legend && layoutAttributes.Highcharts.legend.verticalAlign === 'top') {
      // If we have a title we potentially need to "bump" the y value for the legend down to avoid overlap
      let titleMargin = 15;
      if (layoutAttributes.Highcharts.title && layoutAttributes.Highcharts.title.margin) {
        titleMargin = layoutAttributes.Highcharts.title.margin;
      }
      let currentY = layoutAttributes.Highcharts.legend.y || 0;
      currentY += titleMargin;
      options.legend.y = currentY;
    }

    options.legend = {
      ...options.legend,
      margin: 12,
      title: {
        ...(options.legend.title || {}),
        style: {
          ...((options.legend.title && options.legend.title.style) || {}),
          fontWeight: '600',
          fontSize: '12px'
        }
      },
      itemDistance: 20,
      itemMarginTop: 0,
      itemMarginBottom: 0,
      itemStyle: {
        ...(options.legend.itemStyle || {}),
        fontWeight: '600',
        fontSize: '12px'
      }
    };

    const dataAdapter = this.dataAdapter;
    // eslint:disable-next-line
    options.legend.labelFormatter = function () {
      // eslint:disable-next-line
      return wrapText((dataAdapter.layoutConfig as ChartLayoutConfig), this.name, 'seriesNameLengthLimit', 'seriesNameTruncLimit');
    };
  }

  private removeEmptyData(options: Highcharts.Options) {
    let j = options.series.length;
    while (j--) {
      if ((options.series[j] as any).data.length === 0) {
        options.series.splice(j, 1);
      }
    }
  }

  private setHeight(options: Highcharts.Options) {
    const config = this.dataAdapter.layoutConfig as ChartLayoutConfig;
    if (config.chartStyle('userDefinedHeightEnabled')) {

      const height = this.getExtraGraphHeight(options, this.dataAdapter)
        + (config.chartStyle('userDefinedHeightValue') || defaultChartHeight);
      options.chart.height = Math.min(height, maxChartHeight);
    }
  }

  public getExtraGraphHeight(options: Highcharts.Options, dataAdapter: FavouriteDataAdapter): number {
    return 0;
  }

  public createTitle(splitCol: number, splitRow: FavouriteRowValue, useHtml: boolean) {
    const titleText = this.dataOptions.getColumnTitle(splitCol, 'splitting') + ': ';
    const valueText = this.dataAdapter.format(splitCol, splitRow.value);
    let graphTitle = splitCol >= 0 ?
      wrapText((this.dataAdapter.layoutConfig as ChartLayoutConfig),
        titleText + valueText,
        'subgraphTitleLengthLimit', 'subgraphTitleTruncLimit')
      : null;

    if (useHtml) {
      const iconHtml = this.createIconOrColourDiv(splitCol, splitRow, false);

      // TODO: How to deal with line wrapping?
      graphTitle = this.addSpanWrapper(titleText, 0) +
        (iconHtml.length > 0 ? iconHtml + ' ' : '') +
        this.addSpanWrapper(valueText, this.DEFAULT_ICON_DIMENSIONS);
    }

    return graphTitle;
  }

  private prepareSeries(options: Highcharts.Options, splitCol: number,
    splitRow: FavouriteRowValue, seriesCol: number, seriesValues: FavouriteRowValue[]) {
    const useHtml = this.useHtml(splitCol);

    options.title = { ...options.title, text: this.createTitle(splitCol, splitRow, useHtml), useHTML: useHtml };
    options.series = [];

    this.createAxes(options);

    options.legend = options.legend || {};
    if (this.isLegendEnabled(seriesCol)) {
      this.addLegend(options, seriesCol);
    }
    else {
      options.legend.enabled = false;
    }

    this.addTooltipOptions(options);

    this.addSeriesToColumnMap(options, 'mainSeries');
    options.series = options.series.concat(
      this.seriesDataOptionsBuilder.createSeriesOptions(seriesCol, seriesValues, '', true)
    );

    const appendSeriesValues = this.dataAdapter.selectionCount >= MIN_NO_OF_CRITERIAS;
    if (this.dataAdapter.hasRollingAverage()) {
      this.addSeriesToColumnMap(options, 'rollingAverageSeries');
      options.series = options.series.concat(
        this.seriesDataOptionsBuilder.createSeriesOptions(seriesCol, seriesValues,
          this.dataOptions.getColumnTitle(this.dataAdapter.aggrRollAvgColIndex, 'series', !appendSeriesValues),
          appendSeriesValues, 'line', 'Dot')
      );
    }
    if (this.dataAdapter.hasTarget()) {
      this.addSeriesToColumnMap(options, 'targetSeries');
      options.series = options.series.concat(
        this.seriesDataOptionsBuilder.createSeriesOptions(seriesCol, seriesValues,
          this.dataOptions.getColumnTitle(this.dataAdapter.targetColIndex, 'series', !appendSeriesValues),
          appendSeriesValues, 'line', 'ShortDash')
      );
    }
  }

  private addSeriesToColumnMap(options: Highcharts.Options, seriesType: string) {
    if (!this.columnMap[seriesType]) {
      return;
    }
    this.seriesTypeBaseIndex[seriesType] = options.series.length;
  }

  private reverseDescendingData(options: Highcharts.Options, xIndex: number) {
    for (let j = 0; j < options.series.length; j++) {
      if (this.dataAdapter.columnMetaData[xIndex]?.sortOrderType === SortOrderType.Desc) {
        ((options.series[j] as any).data as Highcharts.Point[]).reverse();
      }
    }
  }

  public colIndexOf(typeName: string, seriesType: string = 'mainSeries') {
    return this.columnMap[seriesType].indexOf(typeName);
  }

  public isXAxisReversedByDefault() {
    return false;
  }

  private createAxes(options: Highcharts.Options) {
    const axisList = ['x', 'y'];
    for (let a = 0; a < axisList.length; a++) {
      const colIndex = this.colIndexOf(axisList[a]);
      const axisName = axisList[a];

      if (colIndex < 0) {
        this.createFakeAxisHandler && this.createFakeAxisHandler(options, axisList[a]);
        continue;
      }

      const axisObj: Highcharts.AxisOptions = {
        lineColor: '#ccd6eb',
        tickColor: '#ccd6eb',
        labels: {
          distance: 15
        }
      };
      this.createAxis(colIndex, axisObj, axisName);
      (options as any)[axisName + 'Axis'] = axisObj;
    }
  }

  public getAxisMin(colIndex: number, dataAdapter: FavouriteDataAdapter): number {
    return dataAdapter.columnExtremes[colIndex].min;
  }

  public getAxisMax(colIndex: number, dataAdapter: FavouriteDataAdapter): number {
    return dataAdapter.columnExtremes[colIndex].max;
  }

  public createAxis(colIndex: number, axisObj: Highcharts.AxisOptions, axisName: string) {
    // eslint:disable-next-line
    const dataOptions = this.dataOptions;
    const dataAdapter = this.dataAdapter;

    axisObj.title = {
      text: dataOptions.getColumnTitle(colIndex, 'axes')
    };

    const maxTicks = (dataOptions.layoutConfig as ChartLayoutConfig).chartStyle('maxTickIn' + axisName.toUpperCase());
    if (dataAdapter.hasCategories(colIndex)) {
      axisObj.type = 'category' as Highcharts.AxisTypeValue;
      axisObj.floor = 0;
      axisObj.ceiling = dataAdapter.columnCategories[colIndex].length - 1;
      axisObj.categories = dataAdapter.columnCategories[colIndex].map(c => {
        return dataAdapter.format(colIndex, c.value);
      });

      if (maxTicks) {
        axisObj.tickInterval = Math.ceil(axisObj.categories.length / maxTicks);
      }

      axisObj.tickmarkPlacement = 'on';
    } else if (dataAdapter.dataModel.columns[colIndex].dataType === 'Date') {
      axisObj.type = 'datetime';
      const min = this.getAxisMin(colIndex, dataAdapter);
      const max = this.getAxisMax(colIndex, dataAdapter);

      if (axisName !== 'x') {
        axisObj.min = min;
        axisObj.max = max;
      }

      axisObj.minTickInterval = setTickInterval(dataAdapter.dataModel.columns[colIndex].period);

      if (maxTicks && (Math.ceil((max - min) / axisObj.minTickInterval) <= maxTicks)) {
        axisObj.tickInterval = axisObj.minTickInterval;
      }
    } else {
      axisObj.type = 'linear';
      const layoutAttributes = this.dataOptions.layoutConfig.layoutAttributes;
      const shouldAdjustMinAndMax = !(layoutAttributes?.Column?.Percent || layoutAttributes?.Column?.Stacked);

      if (shouldAdjustMinAndMax) {
        axisObj.min = this.getAxisMin(colIndex, dataAdapter) >= 0 ? 0 : undefined;
        axisObj.max = this.getAxisMax(colIndex, dataAdapter);
      }

      if (dataAdapter.dataModel.columns[colIndex].dataType === 'Integer'
        || dataAdapter.dataModel.columns[colIndex].dataType === 'BigInteger') {
        axisObj.allowDecimals = false;
        axisObj.minTickInterval = 1;
      }
    }

    // Highcharts requires the x-axis to be in ascending order.
    // We're receiving sorted data from the backend, so we have to reverse descending data, 
    // and then set "reversed" on the chart object to show the data as descending.
    if (dataAdapter.columnMetaData[colIndex]?.sortOrderType === SortOrderType.Desc && axisName === 'x' && axisObj.type !== 'category') {
      axisObj.reversed = !this.isXAxisReversedByDefault();
    }

    axisObj.labels = {
      ...axisObj.labels,
      style: {
        fontSize: '11px'
      },
      formatter: (function (c: any) {
        return function () {
          return dataAdapter.format(c, this.value);
        };
      })(colIndex)
    };

    // adds advanced formatting for labels if Html is allowed, and any axis value contains icon / colour information
    if (this.useHtml(colIndex)) {
      axisObj.labels.useHTML = true;
      // eslint:disable-next-line
      const self = this;

      axisObj.labels.formatter = (function (c: any) {
        return function () {
          // eslint:disable-next-line
          let labelText = escapeHtml(dataAdapter.format(c, this.value));
          if (self.useHtml(c)) {
            // add icon in front of the text
            // eslint:disable-next-line
            const catIndex = this.axis.categories.indexOf(String(this.value));
            if (catIndex >= 0) {
              const cat = dataAdapter.columnCategories[c][catIndex];
              const extraHtml = self.createIconOrColourDiv(c, cat, false);
              if (extraHtml.length > 0) {
                labelText = extraHtml + ' ' + self.addSpanWrapper(labelText, self.DEFAULT_ICON_DIMENSIONS);
              }
            }
          }
          return labelText;
        };
      })(colIndex);
    }

    (axisObj as XAxisOptions).crosshair = true;
    (axisObj as XAxisOptions).lineColor = '#ccd6eb';

    this.createAxisHandler && this.createAxisHandler(colIndex, axisObj, axisName);
  }

  public addTooltipOptions(options: Highcharts.Options) {
    // eslint:disable-next-line
    const self = this;

    options.tooltip = {
      backgroundColor: 'rgba(0,0,0,0)',
      shadow: false,
      borderWidth: 0,
      borderColor: 'rgba(0,0,0,0)',
      formatter: function () {
        return self.tooltipFormat(this);
      },
      style: {
        zIndex: 100
      }
    };

    options.tooltip.useHTML = true;
  }

  //TODO: Fix this abomination.
  private tooltipFormat(sender: any) {
    let text = '';
    let extraHtml = '';
    const dataAdapter = this.dataAdapter;
    const percentageFormatter = dataAdapter.buildNumberFormatter(dataAdapter.intl, true, 1, 'percent');

    const seriesColumn = this.colIndexOf('series');
    if ((seriesColumn >= 0) || (sender.point.seriesType === 'targetSeries') || (sender.point.seriesType === 'rollingAverageSeries')) {
      text += '<span class="' + styles.tooltipBold + '">' + escapeHtml(sender.series.name) + '</span>';
      if (this.useHtml(seriesColumn)) {
        // First try to look up the category in the cached categories array
        const cat = this.dataAdapter.columnCategories[seriesColumn].find(
          (currentCat) => { return this.dataAdapter.format(seriesColumn, currentCat.value) === sender.series.name }
        );
        extraHtml = this.createIconOrColourDiv(seriesColumn, cat, false, sender.series.color);
        if (extraHtml.length > 0) {
          text = extraHtml + ' ' + this.addSpanWrapper(text, 0);
        }
      }
    }

    for (let i = 0; i < this.dataAdapter.dataModel.columns.length; i++) {
      const colType = this.columnMap.mainSeries[i];
      if (!colType || colType === 'series' || sender.point[colType] === undefined) {
        continue;
      }
      if (text.length > 0) {
        text += '<br />';
      }
      let value = sender.point[colType];
      let formatColumn = i;
      let columnCat: FavouriteRowValue;
      let percentageValue = '';
      if ((colType === 'x' || colType === 'y') && this.dataAdapter.hasCategories(i)) {
        columnCat = this.dataAdapter.columnCategories[i][value];
        value = this.dataAdapter.format(i, (columnCat && columnCat.value) || value);
      } else if (colType === 'y') {
        percentageValue = this.dataAdapter.showPercentageInTooltip && !!sender.percentage ?
          ' (' + percentageFormatter(sender.percentage) + ')' : '';
        if (this.dataAdapter.hasTarget && (sender.point.seriesType === 'targetSeries')) {
          formatColumn = this.dataAdapter.targetColIndex;
        } else if (this.dataAdapter.hasRollingAverage() && (sender.point.seriesType === 'rollingAverageSeries')) {
          formatColumn = this.dataAdapter.aggrRollAvgColIndex;
        }
      }
      const titleText = escapeHtml(this.dataOptions.getColumnTitle(formatColumn, 'tooltip')) + ': ';
      const valueText = ' <span class="' + styles.tooltipBold + '">'
        + escapeHtml(this.dataAdapter.format(formatColumn, value)) + percentageValue + '</span>';
      extraHtml = '';
      if (this.useHtml(i)) {
        extraHtml = this.createIconOrColourDiv(i, columnCat, false);
        if (extraHtml.length > 0) {
          text += this.addSpanWrapper(titleText, 0) +
            extraHtml +
            this.addSpanWrapper(valueText, this.DEFAULT_ICON_DIMENSIONS);
        } else {
          text += titleText + valueText;
        }
      } else {
        text += titleText + valueText;
      }
    }
    const style = escapeHtml('background-color: #EBEBEB;color: #4B4949;padding: 5px 10px 5px 10px;border: solid #DADADA 1px;');

    return `<div style="${style}">${text}</div>`;
  }

  public isPercentEnabledForBarColumnAreaChart(): boolean {
    const layoutAttributes = this.dataAdapter.layoutConfig.layoutAttributes;
    const isMinNoOfCriterias = this.dataAdapter.numberOfGroupByFields >= MIN_NO_OF_CRITERIAS;
    return isMinNoOfCriterias && (!!layoutAttributes.Column?.Percent || !!layoutAttributes.Bar?.Percent
      || !!layoutAttributes.Area?.Percent);
  }

  public isStackedEnabledForBarColumnAreaChart(): boolean {
    const layoutAttributes = this.dataAdapter.layoutConfig.layoutAttributes;
    const isMinNoOfCriterias = this.dataAdapter.numberOfGroupByFields >= MIN_NO_OF_CRITERIAS;

    return isMinNoOfCriterias && (!!layoutAttributes.Column?.Stacked ||
      !!layoutAttributes.Bar?.Stacked || !!layoutAttributes.Area?.Stacked);
  }

  public stackedAreaChartHasMultipleUom(): boolean {
    const layoutAttributes = this.dataAdapter.layoutConfig.layoutAttributes;
    const isAreaChartStacked: boolean = !!layoutAttributes.Area?.Stacked;
    const hasUomDomainAggregationColumn = this.dataAdapter.dataModel.columns.some(
      column => column.columnType === ColumnType.aggregation && !!column.uomDomain
    );
    const hasMultipleUom = this.dataAdapter.columnCategories[this.dataAdapter.selectionColIndex]?.length > 1;

    return isAreaChartStacked && hasUomDomainAggregationColumn && hasMultipleUom;
  }

  // Hide series in stacked, percent charts for unsupported aggregation type
  public hideSeriesIfUnsupportedAggrType(options: Highcharts.Options) {
    const isPercentEnabled = this.isPercentEnabledForBarColumnAreaChart();
    const isStackedEnabled = this.isStackedEnabledForBarColumnAreaChart();
    const { aggregationType } = this.dataAdapter.dataModel.columns[this.dataAdapter.aggrColIndex];
    const { setChartWarningMessage } = this.dataAdapter;
    const averageMinimumMaximumAggrTypesEnabled =
      aggregationType === AggregationType.Average ||
      aggregationType === AggregationType.Maximum ||
      aggregationType === AggregationType.Minimum;

    const daysSinceAggrTypesEnabled =
      aggregationType === AggregationType.DaysSinceFirst ||
      aggregationType === AggregationType.DaysSinceLast;

    const hasRollingAverage = this.dataAdapter.hasRollingAverage();
    const hasTarget = this.dataAdapter.hasTarget();
    const isFrequencyReport = this.dataAdapter.dataModel.report === reportTypes.frequencyReport;
    const { translation } = this.dataAdapter;
    const setWarningAndHideSeries = (message: string) => {
      setChartWarningMessage(message);
      options.plotOptions.series.visible = false;
    };

    if (this.stackedAreaChartHasMultipleUom()) {
      setWarningAndHideSeries(translation.area_chart_uom_warning);
    } else if (isPercentEnabled && !isStackedEnabled) {
      setWarningAndHideSeries(translation.column_chart_percentage_warning);
    } else if (isPercentEnabled && isStackedEnabled) {
      if (isFrequencyReport) {
        setWarningAndHideSeries(translation.frequency_report_percent_warning);
      } else if (averageMinimumMaximumAggrTypesEnabled) {
        setWarningAndHideSeries(translation.average_minimum_maximum_warning);
      } else if (daysSinceAggrTypesEnabled) {
        setWarningAndHideSeries(translation.days_since_aggr_warning);
      } else if (hasRollingAverage) {
        setWarningAndHideSeries(translation.rolling_average_warning);
      } else if (hasTarget) {
        setWarningAndHideSeries(translation.target_warning);
      }
    }
  }
}

export function createDefaultChartDataOptionsBuilder(
  dataOptions: FavouriteDataOptions,
  dataAdapter: FavouriteDataAdapter,
  drillDownHandler: DrillDownHandler,
  columnMappings: SeriesTypeColumnMappings
): DefaultChartDataOptionsBuilder {
  const seriesOptionsBuilder = new ChartSeriesDataOptionsBuilder(dataAdapter, drillDownHandler);

  const pointOptionsBuilder = new ChartPointDataOptionsBuilder(
    dataAdapter,
    seriesOptionsBuilder
  );

  return new DefaultChartDataOptionsBuilder(
    dataOptions,
    dataAdapter,
    seriesOptionsBuilder,
    pointOptionsBuilder,
    columnMappings
  );
}
